import { useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useParams } from 'react-router-dom'

import { invitePartnerToKazoo, deletePartnerFromKazoo, getKazooMembers } from '../../features/kazooEventSlice'
import InputCustom from '../atoms/input/InputCustom'
import ButtonCustom from '../atoms/button/ButtonCustom'
import LeaveModal from '../atoms/modal/LeaveConfirmationModal'

import styles from './InviteKazooPlanner.module.scss'
import { toastHelper } from '../../util/toastHelper'
import { EMAIL_RE } from '../../util/constants'

export const InviteKazooPlanner = () => {
	const { kazooId } = useParams()
	const dispatch = useDispatch()
	const kazooMembers = useSelector((state) => state.kazooEvent?.kazooMembers || { bride: {}, groom: {} })
	const bride = kazooMembers.bride
	const groom = kazooMembers.groom
	
	const [partner1, setPartner1] = useState({
		firstName: '',
		lastName: '',
		email: ''
	})
	
	const [partner2, setPartner2] = useState({
		firstName: '',
		lastName: '',
		email: ''
	})

	const [removeModal, setRemoveModal] = useState({ open: false, email: null, partner: null })

	const handlePartner1Change = (e) => {
		setPartner1({ ...partner1, [e.target.name]: e.target.value })
	}

	const handlePartner2Change = (e) => {
		setPartner2({ ...partner2, [e.target.name]: e.target.value })
	}

	const handlePartner1Submit = (e) => {
		e.preventDefault()
		if (!EMAIL_RE.test(partner1.email)) {
			return toastHelper('Invalid email', 'error')
		}
		// Only send email to API, firstName and lastName are for UI only
		dispatch(invitePartnerToKazoo({ 
			kazooId, 
			email: partner1.email.toLowerCase()
		})).then((result) => {
			if (result.payload?.success) {
				setPartner1({ firstName: '', lastName: '', email: '' })
				dispatch(getKazooMembers(kazooId))
			}
		})
	}

	const handlePartner2Submit = (e) => {
		e.preventDefault()
		if (!EMAIL_RE.test(partner2.email)) {
			return toastHelper('Invalid email', 'error')
		}
		// Only send email to API, firstName and lastName are for UI only
		dispatch(invitePartnerToKazoo({ 
			kazooId, 
			email: partner2.email.toLowerCase()
		})).then((result) => {
			if (result.payload?.success) {
				setPartner2({ firstName: '', lastName: '', email: '' })
				dispatch(getKazooMembers(kazooId))
			}
		})
	}

	const handleResendInvite = (email) => {
		// Resend invitation without calling getKazooMembers to avoid re-render
		dispatch(invitePartnerToKazoo({ kazooId, email }))
	}

	const openRemoveModal = (email, partner) => {
		setRemoveModal({ open: true, email, partner })
	}

	const closeRemoveModal = () => {
		setRemoveModal({ open: false, email: null, partner: null })
	}

	const handleRemovePartner = () => {
		if (removeModal.email) {
			dispatch(deletePartnerFromKazoo({ kazooId, email: removeModal.email })).then((result) => {
				if (result.payload?.success) {
					dispatch(getKazooMembers(kazooId))
				}
			})
			closeRemoveModal()
		}
	}

	return (
		<>
			<div className={styles.container}>
				{/* Partner 1 Form */}
				<div className={styles.partnerColumn}>
					<h3 className={styles.partnerTitle}>Partner 1</h3>
					{!bride?.email && !bride?._id ? (
						<>
							<p className={styles.subtitle}>Please enter their email to send an invite</p>
							<form onSubmit={handlePartner1Submit} className={styles.partnerForm}>
								<div className={styles.nameInputs}>
									<InputCustom
										data-cy="partner1-firstname"
										placeholder="Enter first name"
										name="firstName"
										type="text"
										value={partner1.firstName}
										onChange={handlePartner1Change}
										required={true}
									/>
									<InputCustom
										data-cy="partner1-lastname"
										placeholder="Enter last name"
										name="lastName"
										type="text"
										value={partner1.lastName}
										onChange={handlePartner1Change}
										required={true}
									/>
								</div>
								<InputCustom
									data-cy="partner1-email"
									placeholder="Email"
									name="email"
									type="email"
									value={partner1.email}
									onChange={handlePartner1Change}
									required={true}
								/>
								<ButtonCustom
									data-cy="partner1-submit-button"
									type="submit"
									className={styles.inviteBtn}
								>
									Send Invite
								</ButtonCustom>
							</form>
						</>
					) : bride?._id && !bride?.status ? (
						<>
							<p className={styles.inviteStatus}>
								<strong>{bride?.email}</strong> has accepted the invitation!
							</p>
							{/* Only show Remove button if both partners haven't accepted yet */}
							{!(groom?._id && !groom?.status) && (
								<div className={styles.buttonGroup}>
									<ButtonCustom
										data-cy="partner1-remove-button"
										variant="destructive"
										onClick={() => openRemoveModal(bride?.email, 'Partner 1')}
										className={styles.removeBtn}
									>
										Remove
									</ButtonCustom>
								</div>
							)}
						</>
					) : (
						<>
							<p className={styles.inviteStatus}>
								An invite was sent to <strong>{bride?.email}</strong>
							</p>
							<div className={styles.buttonGroup}>
								<ButtonCustom
									data-cy="partner1-resend-button"
									onClick={() => handleResendInvite(bride?.email)}
									className={styles.resendBtn}
								>
									Resend Sent
								</ButtonCustom>
								<ButtonCustom
									data-cy="partner1-remove-button"
									variant="destructive"
									onClick={() => openRemoveModal(bride?.email, 'Partner 1')}
									className={styles.removeBtn}
								>
									Remove
								</ButtonCustom>
							</div>
						</>
					)}
				</div>

				{/* Partner 2 Form */}
				<div className={styles.partnerColumn}>
					<h3 className={styles.partnerTitle}>Partner 2</h3>
					{!groom?.email && !groom?._id ? (
						<>
							<p className={styles.subtitle}>Please enter their email to send an invite</p>
							<form onSubmit={handlePartner2Submit} className={styles.partnerForm}>
								<div className={styles.nameInputs}>
									<InputCustom
										data-cy="partner2-firstname"
										placeholder="Enter first name"
										name="firstName"
										type="text"
										value={partner2.firstName}
										onChange={handlePartner2Change}
										required={true}
									/>
									<InputCustom
										data-cy="partner2-lastname"
										placeholder="Enter last name"
										name="lastName"
										type="text"
										value={partner2.lastName}
										onChange={handlePartner2Change}
										required={true}
									/>
								</div>
								<InputCustom
									data-cy="partner2-email"
									placeholder="Email"
									name="email"
									type="email"
									value={partner2.email}
									onChange={handlePartner2Change}
									required={true}
								/>
								<ButtonCustom
									data-cy="partner2-submit-button"
									type="submit"
									className={styles.inviteBtn}
								>
									Send Invite
								</ButtonCustom>
							</form>
						</>
					) : groom?._id && !groom?.status ? (
						<>
							<p className={styles.inviteStatus}>
								<strong>{groom?.email}</strong> has accepted the invitation!
							</p>
							{/* Only show Remove button if both partners haven't accepted yet */}
							{!(bride?._id && !bride?.status) && (
								<div className={styles.buttonGroup}>
									<ButtonCustom
										data-cy="partner2-remove-button"
										variant="destructive"
										onClick={() => openRemoveModal(groom?.email, 'Partner 2')}
										className={styles.removeBtn}
									>
										Remove
									</ButtonCustom>
								</div>
							)}
						</>
					) : (
						<>
							<p className={styles.inviteStatus}>
								An invite was sent to <strong>{groom?.email}</strong>
							</p>
							<div className={styles.buttonGroup}>
								<ButtonCustom
									data-cy="partner2-resend-button"
									onClick={() => handleResendInvite(groom?.email)}
									className={styles.resendBtn}
								>
									Resend Sent
								</ButtonCustom>
								<ButtonCustom
									data-cy="partner2-remove-button"
									variant="destructive"
									onClick={() => openRemoveModal(groom?.email, 'Partner 2')}
									className={styles.removeBtn}
								>
									Remove
								</ButtonCustom>
							</div>
						</>
					)}
				</div>
			</div>

			<LeaveModal
				open={removeModal.open}
				onClose={closeRemoveModal}
				onLeave={handleRemovePartner}
				header={`Remove ${removeModal.partner}`}
				subHeader={`Are you sure you want to remove the invite sent to ${removeModal.email}?`}
				confirmBtnTxt="Confirm"
			/>
		</>
	)
}
